import {
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Plus,
  RotateCcw,
  Search,
  SearchX,
  X,
} from 'lucide-react'
import { Button } from '../../../ui/Button/Button'
import { Skeleton } from '../../../ui/Skeleton/Skeleton'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import {
  patientFilters,
  type PatientListItem,
  type PatientOperationalStates,
  type ServerPatientFilter,
} from '../patients-model'
import styles from '../PatientsPage.module.css'
import { PatientEmptyState } from './PatientEmptyState'
import { PatientRow } from './PatientRow'

const SKELETON_ROWS = 6

function LoadingRows() {
  return (
    <>
      <p className={styles.srOnly} role="status">
        Loading patients
      </p>
      <ul className={styles.patientList} aria-hidden="true">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <li
            key={index}
            className={`${styles.patientRow} ${styles.skeletonRow}`}
          >
            <div className={styles.patientCell}>
              <span className={styles.avatarPlaceholder} />
              <div className={styles.skeletonStack}>
                <Skeleton width="9.5rem" height="0.875rem" />
                <Skeleton width="6rem" height="0.625rem" />
              </div>
            </div>
            <div className={styles.skeletonStack}>
              <Skeleton width="7.5rem" height="0.75rem" />
              <Skeleton width="10rem" height="0.625rem" />
            </div>
            <div className={`${styles.skeletonStack} ${styles.visitCell}`}>
              <Skeleton width="8rem" height="0.75rem" />
              <Skeleton width="5rem" height="0.625rem" />
            </div>
            <div className={styles.skeletonStack}>
              <Skeleton width="6rem" height="0.75rem" />
              <Skeleton width="11rem" height="0.625rem" />
            </div>
            <span />
          </li>
        ))}
      </ul>
    </>
  )
}

export function PatientList({
  status,
  patients,
  totalPatients,
  startIndex,
  hasPrevious,
  hasNext,
  refreshing,
  onPrevious,
  onNext,
  onSelect,
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  onAdd,
  onRetry,
  onClearFilters,
  operationalStates,
  onSendSms,
}: {
  status: 'loading' | 'ready' | 'forbidden' | 'error'
  patients: PatientListItem[]
  totalPatients: number
  startIndex: number
  hasPrevious: boolean
  hasNext: boolean
  refreshing: boolean
  onPrevious: () => void
  onNext: () => void
  onSelect: (patient: PatientListItem) => void
  searchQuery: string
  onSearchChange: (value: string) => void
  filter: ServerPatientFilter
  onFilterChange: (value: ServerPatientFilter) => void
  onAdd: () => void
  onRetry?: () => void
  onClearFilters?: () => void
  operationalStates: PatientOperationalStates
  onSendSms?: (patient: PatientListItem) => void
}) {
  const narrowed = Boolean(searchQuery.trim()) || filter !== 'all'
  // Column labels only above rows (or their skeletons), never above a state.
  const showColumns =
    status === 'loading' || (status === 'ready' && patients.length > 0)
  return (
    <section className={styles.directoryPanel} aria-label="Practice patients">
      <header className={styles.directoryHeader}>
        <div className={styles.directoryHeading}>
          <span className={styles.eyebrow}>Synced from NexHealth</span>
          <h2>
            Patient directory
            {status === 'ready' && !narrowed ? (
              <span className={styles.countBadge}>
                {totalPatients.toLocaleString()}
              </span>
            ) : null}
          </h2>
          <p>
            Search by name, phone or email. Open a patient for appointments,
            reminders and notes.
          </p>
        </div>
        <Button type="button" onClick={onAdd} className={styles.addButton}>
          <Plus size={16} aria-hidden="true" /> Add patient
        </Button>
      </header>
      <div className={styles.toolbar}>
        <label className={styles.searchBox}>
          <Search size={16} aria-hidden="true" />
          <span className={styles.srOnly}>Search patients</span>
          <input
            type="search"
            value={searchQuery}
            placeholder="Search by name, phone or email"
            onChange={(event) => onSearchChange(event.target.value)}
          />
          {refreshing && searchQuery ? (
            <LoaderCircle
              className={styles.searchSpinner}
              size={15}
              aria-hidden="true"
            />
          ) : null}
          {searchQuery ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
            >
              <X size={15} aria-hidden="true" />
            </button>
          ) : null}
        </label>
        <div
          className={styles.filterBar}
          role="group"
          aria-label="Patient filters"
        >
          {patientFilters.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={filter === item.value}
              onClick={() => onFilterChange(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {showColumns ? (
        <div className={styles.tableHeader} aria-hidden="true">
          <span>Patient</span>
          <span>Contact</span>
          <span>Next visit</span>
          <span>Last Flio interaction</span>
          <span>Actions</span>
        </div>
      ) : null}
      <div className={styles.listBody} aria-busy={status === 'loading'}>
        {status === 'loading' ? (
          <LoadingRows />
        ) : status === 'forbidden' ? (
          <div className={styles.stateContainer}>
            <StatePanel
              kind="forbidden"
              title="Patients unavailable"
              description="You do not have permission to view this clinic's patients."
            />
          </div>
        ) : status === 'error' ? (
          <div className={styles.stateContainer}>
            <StatePanel
              kind="error"
              title="Patients unavailable"
              description="Practice patient records could not be loaded."
            />
            {onRetry ? (
              <Button type="button" variant="secondary" onClick={onRetry}>
                <RotateCcw size={15} aria-hidden="true" /> Try again
              </Button>
            ) : null}
          </div>
        ) : patients.length === 0 && narrowed ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <SearchX size={22} />
            </span>
            <h2>No patients match this search</h2>
            <p>
              Check the spelling, try a phone number or email, or clear the
              filters. A patient who isn’t in the practice system yet can be
              added.
            </p>
            <div className={styles.emptyActions}>
              {onClearFilters ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClearFilters}
                >
                  Clear search and filters
                </Button>
              ) : null}
              <Button type="button" variant="ghost" onClick={onAdd}>
                <Plus size={15} aria-hidden="true" /> Add patient
              </Button>
            </div>
          </div>
        ) : patients.length === 0 ? (
          <PatientEmptyState />
        ) : (
          <ul className={styles.patientList}>
            {patients.map((patient) => {
              const state = patient.interactionAggregateId
                ? operationalStates[patient.interactionAggregateId]
                : undefined
              return (
                <PatientRow
                  key={patient.patientAuthorityId}
                  patient={patient}
                  operationalState={state}
                  onSelect={() => onSelect(patient)}
                  onSendSms={
                    patient.smsContactCapability?.available && onSendSms
                      ? () => onSendSms(patient)
                      : undefined
                  }
                />
              )
            })}
          </ul>
        )}
      </div>
      {status === 'ready' && (patients.length > 0 || hasPrevious) ? (
        <footer className={styles.directoryFooter}>
          <span className={styles.rangeLabel}>
            {patients.length > 0
              ? `${startIndex + 1}–${startIndex + patients.length}`
              : '0'}{' '}
            of {totalPatients.toLocaleString()} patients
            {narrowed ? ' · filtered' : ''}
            {refreshing ? ' · loading' : ''}
          </span>
          <span className={styles.paginationActions}>
            <Button
              type="button"
              variant="secondary"
              disabled={!hasPrevious || refreshing}
              onClick={onPrevious}
            >
              <ChevronLeft size={14} aria-hidden="true" /> Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!hasNext || refreshing}
              onClick={onNext}
            >
              Next <ChevronRight size={14} aria-hidden="true" />
            </Button>
          </span>
        </footer>
      ) : null}
    </section>
  )
}
